import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  adminLogin,
  adminLogout,
  clearStoredAdminAuth,
  fetchAdminMe,
  readStoredAdminAuth,
  writeStoredAdminAuth,
  type AdminUser,
} from '../services/adminApi';

interface AdminAuthContextValue {
  admin: AdminUser | null;
  loading: boolean;
  signIn: (loginName: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AdminAuthContext = createContext<AdminAuthContextValue | null>(null);

export function AdminAuthProvider({ children }: { children: ReactNode }) {
  const [admin, setAdmin] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const stored = readStoredAdminAuth();
      if (!stored) {
        if (!cancelled) setLoading(false);
        return;
      }
      try {
        const me = await fetchAdminMe();
        if (!cancelled) {
          setAdmin(me);
          writeStoredAdminAuth({ token: stored.token, admin: me });
        }
      } catch {
        clearStoredAdminAuth();
        if (!cancelled) setAdmin(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const signIn = useCallback(async (loginName: string, password: string) => {
    const data = await adminLogin(loginName, password);
    writeStoredAdminAuth(data);
    setAdmin(data.admin);
  }, []);

  const signOut = useCallback(async () => {
    await adminLogout();
    setAdmin(null);
  }, []);

  const value = useMemo(
    () => ({ admin, loading, signIn, signOut }),
    [admin, loading, signIn, signOut],
  );

  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>;
}

// 与 AuthContext 同模式：Provider + hook 同文件；抑制 react-refresh 误报
// eslint-disable-next-line react-refresh/only-export-components -- hook 与 Provider 配对导出
export function useAdminAuth(): AdminAuthContextValue {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error('useAdminAuth must be used within AdminAuthProvider');
  return ctx;
}
